import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Lock, LogOut, RefreshCw, Download, Search, Inbox } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Leads() {
  const [token, setToken] = useState(() => sessionStorage.getItem("yesai-admin-token") || "");
  const [password, setPassword] = useState("");
  const [leads, setLeads] = useState(null);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async (t) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/demo-requests`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (res.status === 401) {
        sessionStorage.removeItem("yesai-admin-token");
        setToken("");
        setLeads(null);
        toast.error("Session expired. Please sign in again.");
        return;
      }
      if (!res.ok) throw new Error("failed");
      setLeads(await res.json());
    } catch (e) {
      toast.error("Couldn't load leads. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) throw new Error("bad password");
      const data = await res.json();
      sessionStorage.setItem("yesai-admin-token", data.token);
      setToken(data.token);
      setPassword("");
      toast.success("Welcome back!");
    } catch (err) {
      toast.error("Incorrect password.");
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem("yesai-admin-token");
    setToken("");
    setLeads(null);
  };

  const filtered = (leads || []).filter((l) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [l.name, l.company, l.mobile, l.email, l.message]
      .filter(Boolean)
      .some((v) => v.toLowerCase().includes(q));
  });

  const todayCount = (leads || []).filter(
    (l) => new Date(l.created_at).toDateString() === new Date().toDateString()
  ).length;

  const exportCsv = () => {
    const rows = [["Date", "Name", "Company", "Mobile", "Email", "Message"]];
    filtered.forEach((l) =>
      rows.push([
        new Date(l.created_at).toLocaleString("en-IN"),
        l.name,
        l.company,
        l.mobile,
        l.email,
        (l.message || "").replace(/\n/g, " "),
      ])
    );
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `yesai-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!token) {
    return (
      <div className="pt-14 sm:pt-20 pb-24 sm:pb-32" data-testid="leads-page">
        <div className="max-w-md mx-auto px-5">
          <form
            onSubmit={login}
            className="bg-white border border-slate-200/80 rounded-[2rem] shadow-2xl shadow-slate-900/5 p-8 sm:p-10 text-center"
            data-testid="leads-login-panel"
          >
            <img src="/yesai-logo.png" alt="YesAI" className="h-12 w-auto mx-auto" />
            <h1 className="mt-6 font-display font-extrabold text-2xl tracking-tight">Leads Dashboard</h1>
            <p className="mt-2 text-sm text-slate-500">Enter the admin password to view demo enquiries.</p>
            <div className="mt-7 relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Admin password"
                data-testid="leads-password-input"
                className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-11 pr-4 py-3 text-sm outline-none focus:border-brand-red focus:ring-2 focus:ring-brand-red/10 transition-colors duration-200"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="leads-login-btn"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-brand-red hover:bg-brand-redDark disabled:opacity-60 text-white font-bold px-6 py-3.5 rounded-full transition-colors duration-200"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "View Leads"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-14 sm:pt-20 pb-24 sm:pb-32" data-testid="leads-page">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] font-semibold text-brand-red">Admin</p>
            <h1 className="mt-2 font-display font-extrabold tracking-tight text-3xl sm:text-4xl">Demo Enquiries</h1>
          </div>
          <div className="flex items-center gap-2.5">
            <button
              onClick={() => load(token)}
              data-testid="leads-refresh-btn"
              className="inline-flex items-center gap-2 border border-slate-200 hover:border-brand-red hover:text-brand-red text-slate-600 text-sm font-bold px-4 py-2.5 rounded-full transition-colors duration-200"
            >
              <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              onClick={exportCsv}
              data-testid="leads-export-btn"
              className="inline-flex items-center gap-2 bg-brand-red hover:bg-brand-redDark text-white text-sm font-bold px-4 py-2.5 rounded-full transition-colors duration-200"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
            <button
              onClick={logout}
              data-testid="leads-logout-btn"
              className="inline-flex items-center gap-2 border border-slate-200 hover:border-slate-400 text-slate-600 text-sm font-bold px-4 py-2.5 rounded-full transition-colors duration-200"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
          <div className="flex gap-3">
            <div className="bg-brand-redSoft border border-brand-red/10 rounded-2xl px-5 py-3" data-testid="leads-total-stat">
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Total leads</p>
              <p className="font-display font-extrabold text-2xl text-brand-ink">{(leads || []).length}</p>
            </div>
            <div className="bg-brand-redSoft border border-brand-red/10 rounded-2xl px-5 py-3" data-testid="leads-today-stat">
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Today</p>
              <p className="font-display font-extrabold text-2xl text-brand-ink">{todayCount}</p>
            </div>
          </div>
          <div className="relative sm:w-72">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, company, phone..."
              data-testid="leads-search-input"
              className="w-full bg-white border border-slate-200 rounded-full pl-11 pr-4 py-2.5 text-sm outline-none focus:border-brand-red focus:ring-2 focus:ring-brand-red/10 transition-colors duration-200"
            />
          </div>
        </div>

        <div className="mt-6 bg-white border border-slate-200/80 rounded-3xl overflow-hidden" data-testid="leads-table">
          {filtered.length === 0 ? (
            <div className="py-20 text-center">
              <Inbox className="w-10 h-10 text-slate-300 mx-auto" />
              <p className="mt-4 text-sm text-slate-500">{leads ? "No leads match your search." : "Loading..."}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 text-left">
                    <th className="px-5 py-4 font-mono text-[11px] uppercase tracking-wider text-slate-400">Date</th>
                    <th className="px-5 py-4 font-mono text-[11px] uppercase tracking-wider text-slate-400">Name</th>
                    <th className="px-5 py-4 font-mono text-[11px] uppercase tracking-wider text-slate-400">Company</th>
                    <th className="px-5 py-4 font-mono text-[11px] uppercase tracking-wider text-slate-400">Mobile</th>
                    <th className="px-5 py-4 font-mono text-[11px] uppercase tracking-wider text-slate-400">Email</th>
                    <th className="px-5 py-4 font-mono text-[11px] uppercase tracking-wider text-slate-400">Message</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((l) => (
                    <tr key={l.id} className="border-b border-slate-50 hover:bg-brand-redSoft/40 transition-colors duration-150" data-testid={`lead-row-${l.id}`}>
                      <td className="px-5 py-4 text-slate-500 whitespace-nowrap">{new Date(l.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                      <td className="px-5 py-4 font-bold text-brand-ink whitespace-nowrap">{l.name}</td>
                      <td className="px-5 py-4 text-slate-600 whitespace-nowrap">{l.company}</td>
                      <td className="px-5 py-4 text-slate-600 whitespace-nowrap">{l.mobile}</td>
                      <td className="px-5 py-4 text-slate-600">{l.email}</td>
                      <td className="px-5 py-4 text-slate-500 max-w-xs truncate" title={l.message}>{l.message || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
